// src/pages/NFTVerificationPage.js

import React, { useEffect, useState, useContext } from 'react';
import VerifyNFT from '../pages/VerifyNFT'; // Corrected import path
import WalletManagement from '../components/nftVerificationComponents/WalletManagement';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Avatar,
  Snackbar,
  Alert,
  CircularProgress,
} from '@mui/material';
import { styled } from '@mui/system';
import { WalletMultiButton } from '@solana/wallet-adapter-react-ui';
import backgroundImage from '../assets/mindguard_verification_route.jpg'; // Adjust the path accordingly
import axios from 'axios';

// Define color constants based on Home.js and Navbar.js
const COLORS = {
  buttonGradient: 'linear-gradient(to right, #9AC0CD, #4C5C64)', // Same as NavbarButton
  buttonBoxShadow: '0 4px 15px rgba(154, 192, 205, 0.5)', // Same as NavbarButton
  buttonHoverBackground: '#4C5C64', // Same as NavbarButton hover
  buttonHoverBoxShadow: '0 4px 20px rgba(154, 192, 205, 0.7)', // Same as NavbarButton hover
  buttonActiveBackground: '#5A6C45', // Same as NavbarButton active
  cardBackground: 'rgba(0, 0, 0, 0.5)', // Same as FeatureCard in Home.js
  cardBoxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)', // Same as FeatureCard in Home.js
  textPrimary: '#E0E4ED', // Off-White
  textSecondary: '#C2CCDF', // Light Blue
};

const BackgroundBox = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  backgroundImage: `url(${backgroundImage})`,
  backgroundSize: 'cover',
  backgroundPosition: 'center 93%', // Adjust as needed to move the logo up
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundRepeat: 'no-repeat',
}));

const StyledCard = styled(Card)(({ theme }) => ({
  width: '100%',
  maxWidth: 800, // Increased width to accommodate wallet management
  margin: theme.spacing(2),
  padding: theme.spacing(3),
  backgroundColor: COLORS.cardBackground,
  color: COLORS.textPrimay,
  borderRadius: '20px',
  boxShadow: COLORS.cardBoxShadow,
}));

// Customize WalletMultiButton
const CustomWalletMultiButton = styled(WalletMultiButton)(({ theme }) => ({
  backgroundColor: '#728C63 !important', // Muted Teal
  color: '#E0E4ED !important', // Off-White
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(1, 3),
  fontWeight: 'bold',
  boxShadow: 'none',
  '&:hover': {
    backgroundColor: '#6B7F57 !important', // Slightly darker on hover
  },
  '&:active': {
    backgroundColor: '#5A6C45 !important',
  },
}));

// Styled Button similar to Navbar buttons
const StyledButton = styled(Button)(({ theme }) => ({
  background: COLORS.buttonGradient,
  color: '#FFFFFF',
  borderRadius: '20px',
  padding: theme.spacing(1, 4),
  fontWeight: 'bold',
  boxShadow: COLORS.buttonBoxShadow, // Glow effect
  transition: 'all 0.3s ease',
  '&:hover': {
    background: COLORS.buttonHoverBackground,
    boxShadow: COLORS.buttonHoverBoxShadow, // Increased glow on hover
  },
  '&:active': {
    background: COLORS.buttonActiveBackground, // Slightly darker on active
  },
}));

const NFTVerificationPage = () => {
  const { accessToken, user, discordUserId, guildId, threadId } = useContext(AuthContext); // Ensure AuthContext provides necessary data
  const { username, avatar } = user || {};
  const navigate = useNavigate();

  const [wallets, setWallets] = useState([]);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success', // 'success' | 'error' | 'warning' | 'info'
  });
  const [loadingWallets, setLoadingWallets] = useState(true); // To show loading indicator while fetching wallets

  useEffect(() => {
    const fetchWallets = async () => {
      if (accessToken && discordUserId && guildId) {
        try {
          const response = await axios.get(
            `${process.env.REACT_APP_BACKEND_URL}/api/wallets`,
            {
              headers: {
                'x-discord-user-id': discordUserId,
                'x-guild-id': guildId,
              },
            }
          );

          if (response.status === 200) {
            setWallets(response.data.wallets);
          } else {
            setSnackbar({
              open: true,
              message: 'Failed to fetch wallets.',
              severity: 'error',
            });
          }
        } catch (error) {
          console.error('Error fetching wallets:', error.response?.data?.error || error.message);
          setSnackbar({
            open: true,
            message: `Error: ${error.response?.data?.error || 'Failed to fetch wallets.'}`,
            severity: 'error',
          });
        } finally {
          setLoadingWallets(false);
        }
      } else {
        console.warn('Missing tokens for NFT verification. Redirecting to home.');
        setSnackbar({
          open: true,
          message: 'Verification context is missing. Please try again.',
          severity: 'warning',
        });
        setTimeout(() => navigate('/'), 3000);
      }
    };

    fetchWallets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken, discordUserId, guildId]);

  const handleAddWallet = (wallet) => {
    setWallets((prevWallets) => [...prevWallets, wallet]);
  };

  const handleRemoveWallet = (walletAddress) => {
    setWallets((prevWallets) => prevWallets.filter((wallet) => wallet.address !== walletAddress));
  };

  const handleUpdateWallet = (walletAddress, updates) => {
    setWallets((prevWallets) =>
      prevWallets.map((wallet) =>
        wallet.address === walletAddress ? { ...wallet, ...updates } : wallet
      )
    );
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <BackgroundBox>
      <StyledCard>
        <CardContent>
          <Typography
            variant="h4"
            component="div"
            gutterBottom
            align="center"
            sx={{ color: '#E0E4ED' }} // Off-White
          >
            MINDguard NFT Verification
          </Typography>
          <Typography
            variant="body1"
            align="center"
            paragraph
            sx={{ color: '#C2CCDF' }} // Light Blue
          >
            Connect your wallet(s) to verify NFT ownership and gain server roles.
          </Typography>
          <Box display="flex" justifyContent="center" mb={2}>
            <CustomWalletMultiButton />
          </Box>
          {discordUserId && accessToken && guildId ? (
            <>
              <Box display="flex" alignItems="center" justifyContent="center" mb={2}>
                <Avatar
                  src={avatar}
                  alt={username}
                  sx={{ width: 48, height: 48, mr: 1 }}
                />
                <Typography variant="body1" sx={{ color: '#C2CCDF' }}>
                  Logged in as <strong>{username}</strong>
                </Typography>
              </Box>
              {loadingWallets ? (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="100px">
                  <CircularProgress />
                </Box>
              ) : (
                <>
                  <WalletManagement
                    wallets={wallets}
                    onAddWallet={handleAddWallet}
                    onRemoveWallet={handleRemoveWallet}
                  />
                  <VerifyNFT
                    discordUserId={discordUserId}
                    guildId={guildId}
                    threadId={threadId}
                    wallets={wallets}
                    onUpdateWallet={handleUpdateWallet}
                  />
                </>
              )}
            </>
          ) : (
            <Typography variant="body2" align="center" sx={{ color: '#C2CCDF' }}>
              Waiting for Discord verification link...
            </Typography>
          )}
        </CardContent>
      </StyledCard>

      {/* Snackbar Notification */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </BackgroundBox>
  );
};

export default NFTVerificationPage;

